/**
 * imageCompress — réduit une image côté navigateur avant envoi.
 *
 * Les captures d'écran et photos dépassent souvent la limite d'envoi.
 * On redimensionne (max ~1600px) puis on baisse progressivement la qualité
 * jusqu'à passer sous la taille cible, sans dépendance externe.
 */

export interface CompressOptions {
  maxBytes?: number;
  maxDimension?: number;
  mimeType?: string;
}

export async function compressImageFile(
  file: File,
  options: CompressOptions = {},
): Promise<File> {
  const maxBytes = options.maxBytes ?? 950 * 1024; // 950 Ko (limite serveur 1 Mo)
  const maxDimension = options.maxDimension ?? 1600;
  const mimeType = options.mimeType ?? 'image/jpeg';

  // Si l'image est déjà petite et déjà compressable, on la garde telle quelle.
  const isImage = file.type.startsWith('image/');
  if (!isImage) return file;

  try {
    const bitmap = await loadImage(file);
    const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, width, height);

    let quality = 0.9;
    let blob = await canvasToBlob(canvas, mimeType, quality);
    // Baisse la qualité jusqu'à passer sous la cible (max 5 paliers).
    let attempts = 0;
    while (blob && blob.size > maxBytes && quality > 0.4 && attempts < 5) {
      quality -= 0.12;
      blob = await canvasToBlob(canvas, mimeType, quality);
      attempts += 1;
    }
    if (!blob || blob.size >= file.size) return file;

    const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
    const extension = mimeType === 'image/png' ? 'png' : mimeType === 'image/webp' ? 'webp' : 'jpg';
    return new File([blob], `${baseName}.${extension}`, { type: mimeType });
  } catch {
    // En cas d'échec, on renvoie le fichier d'origine (l'appelant gère la limite).
    return file;
  }
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}

function loadImage(file: File): Promise<HTMLImageElement & { width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
}